import ReservasApiService from '@/services/api/ReservasApiService';
import i18n from '@/i18n';
import { TIPOS_DOCUMENTO, TIPO_DOCUMENTO_A_PEDIR } from '@/constants/usuarioConstants';
import { checkDocumento } from '@/helpers/documentoIdentidadUtils';

const { t } = i18n.global;

export async function realizarCheckIn(formulariosInscripcionReservas, toaster, grupoId, instant) {
  try {
    const requests = [];
    for (const formulario of formulariosInscripcionReservas) {
      let error = false;
      if (formulario.checkInPendiente && formulario.checkInEnabled && (!instant || formulario.checkInInstant)) {
        const formDataDocumentacion = formDataDocumentacionValidator(formulario);
        if (formDataDocumentacion === null) {
          error = true;
          console.error('Error al validar los datos de check-in');
        } else {
          requests.push(() => ReservasApiService.postFormularioCheckIn(grupoId, formulario.id, formDataDocumentacion));
        }
      }
      if (
        formulario.datosPersonalesPendiente &&
        formulario.datosPersonalesEnabled &&
        (!instant || formulario.datosPersonalesInstant)
      ) {
        const formDataPersonal = formDataPersonalValidator(formulario);
        if (formDataPersonal === null) {
          error = true;
          console.error('Error al validar los datos personales');
        } else {
          requests.push(() =>
            ReservasApiService.postFormularioDatosPersonales(grupoId, formulario.id, formDataPersonal)
          );
        }
      }
      if (formulario.preguntasEnabled && (!instant || (formulario.preguntasPendiente && formulario.preguntasInstant))) {
        const formDataPreguntas = formDataPreguntasValidator(formulario);
        if (formDataPreguntas === null) {
          error = true;
          console.error('Error al validar las preguntas');
        } else {
          requests.push(() => ReservasApiService.postFormularioPreguntas(grupoId, formulario.id, formDataPreguntas));
        }
      }
      if (error) {
        toaster.error(t('reserva.checkIn.msg.required'));
        return false;
      }
    }
    await Promise.all(requests.map(request => request()));
    return true;
  } catch (error) {
    console.error('Error al realizar el check-in o enviar datos personales:', error);
    toaster.error(t('reserva.checkIn.msg.error'));
    return false;
  }
}

function formDataPreguntasValidator(formulariosInscripcionReserva) {
  const formDataPreguntas = formulariosInscripcionReserva.formDataPreguntas;
  if (!formDataPreguntas.preguntas.every(pregunta => pregunta.respuesta !== null)) {
    return null;
  }
  return formDataPreguntas;
}

function formDataDocumentacionValidator(formulariosInscripcionReserva) {
  const formDataDocumentacion = formulariosInscripcionReserva.formDataDocumentacion;
  const checkIn = formulariosInscripcionReserva.checkIn;
  const requiresTipoDocumento = checkIn.tipoDocumentoAPedir === TIPO_DOCUMENTO_A_PEDIR.DNI_OR_PASAPORTE;

  const requiresDni =
    checkIn.tipoDocumentoAPedir === TIPO_DOCUMENTO_A_PEDIR.DNI ||
    checkIn.tipoDocumentoAPedir === TIPO_DOCUMENTO_A_PEDIR.DNI_AND_PASAPORTE ||
    (checkIn.tipoDocumentoAPedir === TIPO_DOCUMENTO_A_PEDIR.DNI_OR_PASAPORTE &&
      formDataDocumentacion.tipoDocumento === TIPOS_DOCUMENTO.DNI);

  const requiresPasaporte =
    checkIn.tipoDocumentoAPedir === TIPO_DOCUMENTO_A_PEDIR.PASAPORTE ||
    checkIn.tipoDocumentoAPedir === TIPO_DOCUMENTO_A_PEDIR.DNI_AND_PASAPORTE ||
    (checkIn.tipoDocumentoAPedir === TIPO_DOCUMENTO_A_PEDIR.DNI_OR_PASAPORTE &&
      formDataDocumentacion.tipoDocumento === TIPOS_DOCUMENTO.PASAPORTE);

  if (
    (checkIn.nacionalidadEnabled && !formDataDocumentacion.nacionalidad) ||
    (checkIn.fechaNacimientoEnabled && !formDataDocumentacion.fechaNacimiento) ||
    (checkIn.lugarNacimientoEnabled && !formDataDocumentacion.lugarNacimiento) ||
    (requiresTipoDocumento && !formDataDocumentacion.tipoDocumento) ||
    (requiresDni && checkIn.dniEnabled && !formDataDocumentacion.dni) ||
    (requiresDni &&
      checkIn.dniEnabled &&
      formDataDocumentacion.dni &&
      checkIn.paisEnabled &&
      formDataDocumentacion.pais &&
      !checkDocumento(formDataDocumentacion.pais, formDataDocumentacion.dni).isValid) ||
    (requiresPasaporte &&
      checkIn.pasaporteEnabled &&
      formDataDocumentacion.pasaporte &&
      !checkDocumento(formDataDocumentacion.pais, formDataDocumentacion.pasaporte, { passport: true }).isValid) ||
    (requiresDni && checkIn.numSoporteDniEnabled && !formDataDocumentacion.numSoporteDni) ||
    (requiresDni && checkIn.fechaCaducidadDniEnabled && !formDataDocumentacion.fechaCaducidadDni) ||
    (requiresDni && checkIn.fechaExpedicionDniEnabled && !formDataDocumentacion.fechaExpedicionDni) ||
    (requiresPasaporte && checkIn.pasaporteEnabled && !formDataDocumentacion.pasaporte) ||
    (requiresPasaporte && checkIn.numSoportePasaporteEnabled && !formDataDocumentacion.numSoportePasaporte) ||
    (requiresPasaporte && checkIn.fechaCaducidadPasaporteEnabled && !formDataDocumentacion.fechaCaducidadPasaporte) ||
    (requiresPasaporte && checkIn.fechaExpedicionPasaporteEnabled && !formDataDocumentacion.fechaExpedicionPasaporte) ||
    (checkIn.paisEnabled && !formDataDocumentacion.pais) ||
    (checkIn.provinciaEnabled && !formDataDocumentacion.provincia) ||
    (checkIn.poblacionEnabled && !formDataDocumentacion.poblacion) ||
    (checkIn.domicilioEnabled && !formDataDocumentacion.direccion) ||
    (checkIn.codPostalEnabled && !formDataDocumentacion.codigoPostal) ||
    (checkIn.sexoEnabled && !formDataDocumentacion.sexo) ||
    (checkIn.llegadaEnabled && !formDataDocumentacion.horaCheckIn) ||
    (checkIn.salidaEnabled && !formDataDocumentacion.horaCheckOut) ||
    (requiresDni && checkIn.anversoDniEnabled && !formDataDocumentacion.anversoDni) ||
    (requiresDni && checkIn.reversoDniEnabled && !formDataDocumentacion.reversoDni) ||
    (requiresPasaporte && checkIn.anversoPasaporteEnabled && !formDataDocumentacion.anversoPasaporte)
  ) {
    return null;
  }

  const formData = new FormData();
  if (checkIn.nacionalidadEnabled) {
    formData.append('nacionalidad', formDataDocumentacion.nacionalidad);
  }
  if (checkIn.fechaNacimientoEnabled) {
    formData.append('fechaNacimiento', formDataDocumentacion.fechaNacimiento);
  }
  if (checkIn.lugarNacimientoEnabled) {
    formData.append('lugarNacimiento', formDataDocumentacion.lugarNacimiento);
  }
  if (requiresTipoDocumento) {
    formData.append('tipoDocumento', formDataDocumentacion.tipoDocumento);
  }
  if (requiresDni && checkIn.dniEnabled) {
    formData.append('dni', formDataDocumentacion.dni);
  }
  if (requiresDni && checkIn.numSoporteDniEnabled) {
    formData.append('numSoporteDni', formDataDocumentacion.numSoporteDni);
  }
  if (requiresDni && checkIn.fechaCaducidadDniEnabled) {
    formData.append('fechaCaducidadDni', formDataDocumentacion.fechaCaducidadDni);
  }
  if (requiresDni && checkIn.fechaExpedicionDniEnabled) {
    formData.append('fechaExpedicionDni', formDataDocumentacion.fechaExpedicionDni);
  }
  if (requiresPasaporte && checkIn.pasaporteEnabled) {
    formData.append('pasaporte', formDataDocumentacion.pasaporte);
  }
  if (requiresPasaporte && checkIn.numSoportePasaporteEnabled) {
    formData.append('numSoportePasaporte', formDataDocumentacion.numSoportePasaporte);
  }
  if (requiresPasaporte && checkIn.fechaCaducidadPasaporteEnabled) {
    formData.append('fechaCaducidadPasaporte', formDataDocumentacion.fechaCaducidadPasaporte);
  }
  if (requiresPasaporte && checkIn.fechaExpedicionPasaporteEnabled) {
    formData.append('fechaExpedicionPasaporte', formDataDocumentacion.fechaExpedicionPasaporte);
  }
  if (checkIn.sexoEnabled) {
    formData.append('sexo', formDataDocumentacion.sexo);
  }
  if (checkIn.domicilioEnabled) {
    formData.append('direccion', formDataDocumentacion.direccion);
  }
  if (checkIn.codPostalEnabled) {
    formData.append('codigoPostal', formDataDocumentacion.codigoPostal);
  }
  if (checkIn.paisEnabled) {
    formData.append('pais', formDataDocumentacion.pais);
  }
  if (checkIn.provinciaEnabled) {
    formData.append('provincia', formDataDocumentacion.provincia);
  }
  if (checkIn.poblacionEnabled) {
    formData.append('poblacion', formDataDocumentacion.poblacion);
  }
  if (checkIn.llegadaEnabled) {
    formData.append('horaCheckIn', formDataDocumentacion.horaCheckIn);
  }
  if (checkIn.salidaEnabled) {
    formData.append('horaCheckOut', formDataDocumentacion.horaCheckOut);
  }
  if (requiresDni && checkIn.anversoDniEnabled && formDataDocumentacion.anversoDni) {
    formData.append('anversoDni', formDataDocumentacion.anversoDni);
  }
  if (requiresDni && checkIn.reversoDniEnabled && formDataDocumentacion.reversoDni) {
    formData.append('reversoDni', formDataDocumentacion.reversoDni);
  }
  if (requiresPasaporte && checkIn.anversoPasaporteEnabled && formDataDocumentacion.anversoPasaporte) {
    formData.append('anversoPasaporte', formDataDocumentacion.anversoPasaporte);
  }
  return formData;
}

function formDataPersonalValidator(formulariosInscripcionReserva) {
  const formDataPersonal = formulariosInscripcionReserva.formDataPersonal;
  const datosPersonales = formulariosInscripcionReserva.datosPersonales;
  if (
    (datosPersonales.alergiasEnabled && !formDataPersonal.alergias) ||
    (datosPersonales.alturaEnabled && !formDataPersonal.altura) ||
    (datosPersonales.pesoEnabled && !formDataPersonal.peso) ||
    (datosPersonales.tallaPieEnabled && !formDataPersonal.tallaPie)
  ) {
    return null;
  }

  return formDataPersonal;
}
